import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import {
  apiCreateRule,
  apiDeleteRule,
  apiListRules,
  apiTriggerAnomalyGeneration,
  apiUpdateRule,
  AnomalyRule,
  AnomalySeverity,
  RuleCreateBody,
  RuleDirection,
  RulePatchBody,
  RuleType,
  Timeframe,
} from './api';
import { describeRule, humanizeName } from './humanize';

const TIMEFRAMES: Timeframe[] = ['DAY', 'WEEK', 'MONTH', 'YEAR'];
const DIRECTIONS: RuleDirection[] = ['UP', 'DOWN', 'BOTH'];
const SEVERITIES: AnomalySeverity[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const RULE_TYPES: RuleType[] = ['PCT_CHANGE', 'SAME_WEEKDAY_ZSCORE'];

const SEVERITY_STYLE: Record<AnomalySeverity, string> = {
  LOW:      'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700',
  MEDIUM:   'bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-800',
  HIGH:     'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800',
  CRITICAL: 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-800',
};

function severityLabel(s: AnomalySeverity): string {
  return s.charAt(0) + s.slice(1).toLowerCase();
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Mode =
  | { view: 'list' }
  | { view: 'edit'; rule: AnomalyRule }
  | { view: 'create' };

export default function RulesManager({ open, onOpenChange }: Props) {
  const [mode, setMode] = useState<Mode>({ view: 'list' });
  const [rules, setRules] = useState<AnomalyRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiListRules(true);
      setRules(res.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setMode({ view: 'list' });
      void load();
    }
  }, [open, load]);

  const toggleEnabled = useCallback(async (rule: AnomalyRule) => {
    setBusy(`toggle-${rule.id}`);
    setError(null);
    try {
      await apiUpdateRule(rule.id, { enabled: !rule.enabled });
      setRules((prev) => prev.map(r => r.id === rule.id ? { ...r, enabled: !r.enabled } : r));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, []);

  const deleteRule = useCallback(async (rule: AnomalyRule) => {
    if (!window.confirm(`Disable rule "${rule.metric_name}"? It will stop firing but historical anomalies remain.`)) return;
    setBusy(`delete-${rule.id}`);
    setError(null);
    try {
      await apiDeleteRule(rule.id);
      setRules((prev) => prev.map(r => r.id === rule.id ? { ...r, enabled: false } : r));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, []);

  const regenerate = useCallback(async () => {
    if (!window.confirm('Ask the LLM to propose more rules for this tenant? This calls OpenRouter and can take ~30-60s.')) return;
    setBusy('regenerate');
    setError(null);
    try {
      const res = await apiTriggerAnomalyGeneration();
      await load();
      window.alert(`LLM generated ${res.rules_persisted} new rule(s).`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, [load]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
        {mode.view === 'list' ? (
          <ListView
            rules={rules}
            loading={loading}
            busy={busy}
            error={error}
            onRefresh={() => void load()}
            onRegenerate={() => void regenerate()}
            onNew={() => setMode({ view: 'create' })}
            onEdit={(rule) => setMode({ view: 'edit', rule })}
            onToggle={(rule) => void toggleEnabled(rule)}
            onDelete={(rule) => void deleteRule(rule)}
          />
        ) : (
          <RuleForm
            initial={mode.view === 'edit' ? mode.rule : null}
            onCancel={() => setMode({ view: 'list' })}
            onSaved={() => {
              setMode({ view: 'list' });
              void load();
            }}
            onError={setError}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}


// ── List view ────────────────────────────────────────────────────────────────

interface ListViewProps {
  rules: AnomalyRule[];
  loading: boolean;
  busy: string | null;
  error: string | null;
  onRefresh: () => void;
  onRegenerate: () => void;
  onNew: () => void;
  onEdit: (rule: AnomalyRule) => void;
  onToggle: (rule: AnomalyRule) => void;
  onDelete: (rule: AnomalyRule) => void;
}

function ListView({ rules, loading, busy, error, onRefresh, onRegenerate, onNew, onEdit, onToggle, onDelete }: ListViewProps) {
  return (
    <>
      <SheetHeader>
        <SheetTitle>Manage Rules</SheetTitle>
        <SheetDescription>
          Toggle rules on/off, tune thresholds, or add custom rules. LLM-generated rules can be regenerated at any time.
        </SheetDescription>
      </SheetHeader>

      <div className="flex flex-wrap gap-2 mt-4">
        <Button size="sm" variant="default" onClick={onNew}>+ New rule</Button>
        <Button size="sm" variant="outline" onClick={onRegenerate} disabled={busy === 'regenerate'}>
          {busy === 'regenerate' ? 'Generating…' : 'Regenerate from LLM'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onRefresh} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {error ? (
        <div className="mt-4 p-3 border border-red-500 rounded text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <div className="mt-4 space-y-2">
        {rules.length === 0 && !loading ? (
          <div className="text-center py-10 text-muted-foreground text-sm">
            No rules yet. Click "Regenerate from LLM" to have the LLM propose starter rules,
            or add one manually.
          </div>
        ) : null}
        {rules.map((r) => (
          <div
            key={r.id}
            className={`border rounded-lg p-4 ${r.enabled ? '' : 'opacity-60'}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-semibold text-base truncate">{humanizeName(r.metric_name)}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${SEVERITY_STYLE[r.severity]}`}>
                    {severityLabel(r.severity)}
                  </span>
                </div>
                {r.notes ? (
                  <div className="text-sm text-foreground/80 mt-1 leading-snug">
                    {r.notes}
                  </div>
                ) : null}
                <div className="text-sm text-muted-foreground mt-2">
                  {describeRule(r)}
                </div>
                <div className="text-xs text-muted-foreground mt-2 font-mono">
                  Uses <span className="text-foreground/70">{r.table_name}</span>
                  {' · '}
                  <span className="text-foreground/70">{r.date_column}</span>
                </div>
              </div>

              <div className="flex flex-col gap-2 items-end shrink-0">
                <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={r.enabled}
                    disabled={busy === `toggle-${r.id}`}
                    onChange={() => onToggle(r)}
                  />
                  Enabled
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-1 mt-3 pt-3 border-t">
              <Button size="sm" variant="ghost" onClick={() => onEdit(r)}>Edit</Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-red-600 hover:text-red-700"
                onClick={() => onDelete(r)}
                disabled={busy === `delete-${r.id}` || !r.enabled}
              >
                Disable
              </Button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}


// ── Edit / Create form ───────────────────────────────────────────────────────

interface RuleFormProps {
  initial: AnomalyRule | null;
  onCancel: () => void;
  onSaved: () => void;
  onError: (msg: string | null) => void;
}

interface FormState {
  metric_name: string;
  table_name: string;
  is_json: boolean;
  rule_type: RuleType;
  metric_sql: string;
  date_column: string;
  timeframes: Timeframe[];
  direction: RuleDirection;
  severity: AnomalySeverity;
  config: string; // JSON string, validated on save
  notes: string;
}

const DEFAULT_CONFIG: Record<RuleType, string> = {
  PCT_CHANGE: JSON.stringify({ thresholds: { DAY: 50, WEEK: 30, MONTH: 20 }, min_absolute_delta: null }, null, 2),
  SAME_WEEKDAY_ZSCORE: JSON.stringify({ z_threshold: 3.0, weeks_lookback: 8, min_samples: 4 }, null, 2),
};

function stateFromRule(r: AnomalyRule | null): FormState {
  if (!r) {
    return {
      metric_name: '',
      table_name: 'table_json_1',
      is_json: true,
      rule_type: 'PCT_CHANGE',
      metric_sql: 'SELECT COUNT(*) AS value FROM "table_json_1" WHERE 1=1 {{FILTERS}}',
      date_column: 'trx_date',
      timeframes: ['DAY', 'WEEK', 'MONTH'],
      direction: 'BOTH',
      severity: 'MEDIUM',
      config: DEFAULT_CONFIG.PCT_CHANGE,
      notes: '',
    };
  }
  return {
    metric_name: r.metric_name,
    table_name: r.table_name,
    is_json: r.is_json,
    rule_type: r.rule_type,
    metric_sql: r.metric_sql,
    date_column: r.date_column,
    timeframes: r.timeframes,
    direction: r.direction,
    severity: r.severity,
    config: JSON.stringify(r.config ?? {}, null, 2),
    notes: r.notes ?? '',
  };
}

function RuleForm({ initial, onCancel, onSaved, onError }: RuleFormProps) {
  const [form, setForm] = useState<FormState>(() => stateFromRule(initial));
  const [saving, setSaving] = useState(false);

  const isEdit = initial !== null;

  const toggleTimeframe = (tf: Timeframe) => {
    setForm(f => ({
      ...f,
      timeframes: f.timeframes.includes(tf)
        ? f.timeframes.filter(x => x !== tf)
        : [...f.timeframes, tf],
    }));
  };

  const changeRuleType = (rt: RuleType) => {
    setForm(f => ({
      ...f,
      rule_type: rt,
      config: DEFAULT_CONFIG[rt],
      // SAME_WEEKDAY_ZSCORE requires timeframes = ['DAY']
      timeframes: rt === 'SAME_WEEKDAY_ZSCORE' ? ['DAY'] : f.timeframes,
    }));
  };

  const validate = (): string | null => {
    if (!form.metric_name.trim()) return 'metric_name is required';
    if (!form.metric_sql.includes('{{FILTERS}}')) return 'metric_sql must contain the literal {{FILTERS}} placeholder';
    if (!form.date_column.trim()) return 'date_column is required';
    if (form.timeframes.length === 0) return 'at least one timeframe required';
    if (form.rule_type === 'SAME_WEEKDAY_ZSCORE' && (form.timeframes.length !== 1 || form.timeframes[0] !== 'DAY')) {
      return 'SAME_WEEKDAY_ZSCORE rules must have exactly timeframes = ["DAY"]';
    }
    try {
      JSON.parse(form.config);
    } catch {
      return 'config is not valid JSON';
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { onError(err); return; }
    const cfg = JSON.parse(form.config);
    setSaving(true);
    onError(null);
    try {
      if (isEdit && initial) {
        const patch: RulePatchBody = {
          metric_name: form.metric_name,
          metric_sql: form.metric_sql,
          date_column: form.date_column,
          timeframes: form.timeframes,
          direction: form.direction,
          severity: form.severity,
          config: cfg,
          notes: form.notes || null,
        };
        await apiUpdateRule(initial.id, patch);
      } else {
        const body: RuleCreateBody = {
          table_name: form.table_name,
          is_json: form.is_json,
          rule_type: form.rule_type,
          metric_name: form.metric_name,
          metric_sql: form.metric_sql,
          date_column: form.date_column,
          timeframes: form.timeframes,
          direction: form.direction,
          severity: form.severity,
          config: cfg,
          notes: form.notes || null,
        };
        await apiCreateRule(body);
      }
      onSaved();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SheetHeader>
        <SheetTitle>{isEdit ? `Edit rule: ${initial!.metric_name}` : 'New rule'}</SheetTitle>
        <SheetDescription>
          {isEdit
            ? 'Changes take effect on the next cron run. date_column must be a plain identifier; metric_sql is checked and any problem is logged, not blocked.'
            : 'Create a custom rule. table + rule_type are locked once saved.'}
        </SheetDescription>
      </SheetHeader>

      <div className="mt-4 space-y-3 text-sm">
        <Field label="Metric name">
          <Input value={form.metric_name} onChange={(e) => setForm(f => ({ ...f, metric_name: e.target.value }))} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Table">
            <Input
              value={form.table_name}
              disabled={isEdit}
              onChange={(e) => setForm(f => ({ ...f, table_name: e.target.value, is_json: e.target.value.startsWith('table_json_') }))}
            />
          </Field>
          <Field label="Rule type">
            <select
              className="border rounded px-2 py-1 text-sm bg-background w-full disabled:opacity-60"
              value={form.rule_type}
              disabled={isEdit}
              onChange={(e) => changeRuleType(e.target.value as RuleType)}
            >
              {RULE_TYPES.map(rt => <option key={rt} value={rt}>{rt}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Date column">
          <Input value={form.date_column} onChange={(e) => setForm(f => ({ ...f, date_column: e.target.value }))} />
        </Field>

        <Field label="Timeframes">
          <div className="flex gap-3 flex-wrap">
            {TIMEFRAMES.map(tf => (
              <label key={tf} className="flex items-center gap-1 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={form.timeframes.includes(tf)}
                  disabled={form.rule_type === 'SAME_WEEKDAY_ZSCORE' && tf !== 'DAY'}
                  onChange={() => toggleTimeframe(tf)}
                />
                {tf}
              </label>
            ))}
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Direction">
            <select
              className="border rounded px-2 py-1 text-sm bg-background w-full"
              value={form.direction}
              onChange={(e) => setForm(f => ({ ...f, direction: e.target.value as RuleDirection }))}
            >
              {DIRECTIONS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Severity">
            <select
              className="border rounded px-2 py-1 text-sm bg-background w-full"
              value={form.severity}
              onChange={(e) => setForm(f => ({ ...f, severity: e.target.value as AnomalySeverity }))}
            >
              {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>

        <Field label="metric_sql — must contain {{FILTERS}}">
          <Textarea
            className="font-mono text-xs"
            rows={5}
            value={form.metric_sql}
            onChange={(e) => setForm(f => ({ ...f, metric_sql: e.target.value }))}
          />
        </Field>

        <Field label={`config (JSON) — shape depends on rule_type`}>
          <Textarea
            className="font-mono text-xs"
            rows={7}
            value={form.config}
            onChange={(e) => setForm(f => ({ ...f, config: e.target.value }))}
          />
        </Field>

        <Field label="Notes (optional)">
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
        </Field>

        <div className="flex gap-2 justify-end pt-2 border-t">
          <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button onClick={() => void save()} disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create rule')}</Button>
        </div>
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      {children}
    </div>
  );
}
